import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import api from '../services/api';
import ReenviarVerificacion from '../components/ReenviarVerificacion';
import PasswordInput from '../components/PasswordInput';

const Register = () => {
  const [searchParams] = useSearchParams();
  const [planes, setPlanes] = useState([]);
  const [tiposNegocio, setTiposNegocio] = useState([]);
  const [formData, setFormData] = useState({
    nombre_negocio: '',
    tipo_negocio: '',
    email: '',
    telefono: '',
    whatsapp_number: '',
    plan_id: '',
    nombre: '',
    apellido: '',
    password: '',
    confirmar_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [emailPendiente, setEmailPendiente] = useState('');
  const [addons, setAddons] = useState({ crm: false, catalogos: false, envios: false });
  const [draggedAddon, setDraggedAddon] = useState(null);
  const [dropTarget, setDropTarget] = useState(false);
  const { register } = useAuth();

  const ADDONS = [
    { key: 'crm',       label: 'CRM',            icon: '🤝', desc: 'Prospectos y seguimiento desde WhatsApp', price: 10 },
    { key: 'catalogos', label: 'Catálogos',       icon: '🛍️', desc: 'Catálogo de productos con imágenes',     price: 5  },
    { key: 'envios',    label: 'Envíos Masivos',  icon: '📢', desc: 'Campañas masivas por WhatsApp',          price: 10 },
  ];

  const planSeleccionado = planes.find(p => p.id === parseInt(formData.plan_id));
  const isTrial = planSeleccionado?.nombre === 'Trial';
  const addonTotal = (addons.crm ? 10 : 0) + (addons.catalogos ? 5 : 0) + (addons.envios ? 10 : 0);
  const precioBase = parseFloat(planSeleccionado?.precio_mensual || 0);
  const totalFinal = precioBase + addonTotal;

  const toggleAddon = (key) => {
    if (isTrial) return;
    setAddons(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handlePlanSelect = (planId) => {
    const plan = planes.find(p => p.id === planId);
    setFormData({ ...formData, plan_id: planId });
    if (plan?.nombre === 'Trial') setAddons({ crm: false, catalogos: false, envios: false });
  };
  const navigate = useNavigate();

  // Verificar si venimos de un pago exitoso o fallido
  useEffect(() => {
    const paymentStatus = searchParams.get('payment_status');
    const error = searchParams.get('error');
    
    if (paymentStatus === 'success') {
      toast.success('¡Pago exitoso! Tu cuenta ha sido creada. Ahora puedes iniciar sesión.');
      setTimeout(() => navigate('/login'), 2000);
    } else if (paymentStatus === 'failed') {
      toast.error('El pago no se completó. Por favor, intenta nuevamente.');
    } else if (error === 'registration') {
      toast.error('Error al crear la cuenta. Contacta a soporte.');
    }
  }, [searchParams, navigate]);

  useEffect(() => {
    fetchPlanes();
    fetchTiposNegocio();
  }, []);

  const fetchPlanes = async () => {
    try {
      const { data } = await api.get('/planes?activos=true');
      const planesFiltrados = data.filter(p =>
        p.nombre === 'Trial' || p.nombre === 'Básico' || p.nombre === 'Profesional'
      );
      setPlanes(planesFiltrados);
      if (planesFiltrados.length > 0) {
        setFormData(prev => ({ ...prev, plan_id: planesFiltrados[0].id }));
      }
    } catch (error) {
      toast.error('Error al cargar planes');
    }
  };

  const fetchTiposNegocio = async () => {
    try {
      const { data } = await api.get('/tipos-negocio');
      setTiposNegocio(data);
      if (data.length > 0) {
        setFormData(prev => ({ ...prev, tipo_negocio: data[0].nombre }));
      }
    } catch {
      // Si falla, dejar el select vacío — el usuario puede escribir o recargar
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmar_password) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    setLoading(true);
    
    try {
      const planSeleccionado = planes.find(p => p.id === parseInt(formData.plan_id));
      
      if (!planSeleccionado) {
        toast.error('Por favor selecciona un plan');
        setLoading(false);
        return;
      }

      const submitData = {
        ...formData,
        addon_crm:       isTrial ? 0 : (addons.crm       ? 1 : 0),
        addon_catalogos: isTrial ? 0 : (addons.catalogos  ? 1 : 0),
        addon_envios:    isTrial ? 0 : (addons.envios     ? 1 : 0),
      };

      // 🔥 Para planes Trial, registrar directamente sin pago
      if (planSeleccionado.nombre === 'Trial') {
        const result = await register(submitData);
        if (result.success) {
          if (result.requiere_verificacion) {
            setEmailPendiente(formData.email);
          } else {
            toast.success('¡Registro exitoso! Tu periodo de prueba de 30 días ha comenzado.');
            setTimeout(() => navigate('/'), 2000);
          }
        } else {
          toast.error(result.error);
        }
        setLoading(false);
        return;
      }

      // Para planes de pago:
      // PASO 1 — Crear empresa y usuario en BD (suspendido hasta confirmar pago)
      toast.info('Creando tu cuenta...');
      const registerResponse = await api.post('/auth/register', {
        ...submitData,
        estado_inicial: 'suspendido'
      });
      const { tenant_id } = registerResponse.data;

      // PASO 2 — Preparar la transacción (monto = plan + addons)
      const { data } = await api.post('/payments/prepare-registration', {
        planId: planSeleccionado.id,
        monto: totalFinal,
        tenantId: tenant_id,
        datosRegistro: submitData
      });

      // PASO 3 — Ir al checkout
      navigate('/checkout', {
        state: {
          transactionId: data.transactionId,
          plan: planSeleccionado.nombre,
          precio: totalFinal,
          email: formData.email
        }
      });

    } catch (error) {
      console.error('Error en registro:', error);
      toast.error(error.response?.data?.error || 'Error al preparar el registro. Intenta de nuevo.');
      setLoading(false);
    }
  };

  if (emailPendiente) {
    return (
      <div style={{ minHeight:'100vh', background:'linear-gradient(135deg,#f0f9ff 0%,#ffffff 50%,#eff6ff 100%)', display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
        <div style={{ background:'white', borderRadius:'16px', padding:'2.5rem 2rem', maxWidth:'420px', width:'100%', textAlign:'center', boxShadow:'0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize:'1.4rem', fontWeight:'800', color:'#3b82f6', marginBottom:'1.5rem' }}>🎯 FactuTracks</div>
          <div style={{ fontSize:'3.5rem', marginBottom:'1rem' }}>📧</div>
          <h2 style={{ fontSize:'1.3rem', fontWeight:'800', color:'#111827', marginBottom:'0.5rem' }}>
            ¡Revisa tu correo!
          </h2>
          <p style={{ color:'#6b7280', fontSize:'0.9rem', lineHeight:'1.7', marginBottom:'1.5rem' }}>
            Te enviamos un enlace de confirmación a<br />
            <strong style={{ color:'#111827' }}>{emailPendiente}</strong><br /><br />
            Haz clic en el enlace para activar tu cuenta y comenzar tu prueba de 30 días.
          </p>
          <p style={{ color:'#9ca3af', fontSize:'0.8rem', marginBottom:'1.5rem' }}>
            ¿No lo recibiste? Revisa tu carpeta de spam o
          </p>
          <ReenviarVerificacion email={emailPendiente} />
          <Link to="/login" style={{ display:'block', marginTop:'1rem', color:'#6b7280', fontSize:'0.85rem', textDecoration:'underline' }}>
            Volver al login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f0f9ff 0%, #ffffff 50%, #eff6ff 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem 1rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Decoración */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: `repeating-linear-gradient(
          45deg,
          rgba(59, 130, 246, 0.02) 0px,
          rgba(59, 130, 246, 0.02) 20px,
          transparent 20px,
          transparent 40px
        )`,
        pointerEvents: 'none'
      }} />

      <div style={{
        position: 'absolute',
        top: '-150px',
        left: '-100px',
        width: '400px',
        height: '400px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.15) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(40px)',
        pointerEvents: 'none'
      }} />
      
      <div style={{
        position: 'absolute',
        bottom: '-150px',
        right: '-100px',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(60px)',
        pointerEvents: 'none'
      }} />

      <div style={{
        position: 'absolute',
        top: '20%',
        right: '10%',
        width: '100px',
        height: '100px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(20px)',
        pointerEvents: 'none'
      }} />

      <div style={{
        position: 'absolute',
        bottom: '30%',
        left: '5%',
        width: '150px',
        height: '150px',
        background: 'radial-gradient(circle, rgba(59, 130, 246, 0.08) 0%, transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(30px)',
        pointerEvents: 'none'
      }} />

      {/* Header */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        backdropFilter: 'blur(10px)',
        padding: '0.75rem 2rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid rgba(59, 130, 246, 0.1)',
        boxShadow: '0 4px 20px rgba(59, 130, 246, 0.05)',
        zIndex: 1000
      }}>
        <div style={{
          fontSize: '1.5rem',
          fontWeight: '700',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: '#3b82f6'
        }}>
          <span>🎯</span> FactuTracks
        </div>
        
        <a 
          href="https://factutracks.com" 
          style={{
            backgroundColor: 'white',
            color: '#4b5563',
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.625rem 1.25rem',
            borderRadius: '8px',
            border: '1px solid #e5e7eb',
            fontSize: '0.875rem',
            fontWeight: '500',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#f9fafb';
            e.target.style.borderColor = '#93c5fd';
            e.target.style.color = '#3b82f6';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = 'white';
            e.target.style.borderColor = '#e5e7eb';
            e.target.style.color = '#4b5563';
          }}
        >
          <span>←</span> Volver
        </a>
      </div>

      {/* Card de registro */}
      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: '32px',
        boxShadow: '0 25px 50px -12px rgba(59, 130, 246, 0.5)',
        width: '100%',
        maxWidth: '800px',
        padding: '3rem',
        marginTop: '80px',
        border: '1px solid rgba(255, 255, 255, 0.5)',
        position: 'relative',
        overflow: 'hidden',
        zIndex: 10
      }}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: 'linear-gradient(90deg, #3b82f6, #93c5fd, #3b82f6)',
          borderTopLeftRadius: '32px',
          borderTopRightRadius: '32px'
        }} />

        <div style={{
          position: 'absolute',
          top: '20px',
          right: '20px',
          width: '100px',
          height: '100px',
          background: 'radial-gradient(circle, rgba(59, 130, 246, 0.05) 0%, transparent 70%)',
          borderRadius: '50%',
          pointerEvents: 'none'
        }} />

        <h2 style={{
          fontSize: '2rem',
          fontWeight: '800',
          color: '#111827',
          textAlign: 'center',
          marginBottom: '0.5rem'
        }}>
          Crear Cuenta
        </h2>
        <p style={{
          textAlign: 'center',
          color: '#6b7280',
          fontSize: '0.875rem',
          marginBottom: '2rem'
        }}>
          Regístrate y comienza a gestionar tu negocio
        </p>

        <form onSubmit={handleSubmit}>
          {/* Datos del Negocio */}
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '1rem',
            marginTop: '0.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span>🏢</span> Datos del Negocio
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                Nombre del Negocio *
              </label>
              <input
                type="text"
                name="nombre_negocio"
                value={formData.nombre_negocio}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '0.875rem 1.25rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                  outline: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="Ej: Clínica Dental San José"
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                Tipo de Negocio *
              </label>
              <select
                name="tipo_negocio"
                value={formData.tipo_negocio}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '0.875rem 1.25rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                  outline: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  cursor: 'pointer'
                }}
              >
                {tiposNegocio.length === 0 && (
                  <option value="">Cargando...</option>
                )}
                {tiposNegocio.map(t => (
                  <option key={t.id} value={t.nombre}>{t.icono} {t.nombre}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                Email del Negocio *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '0.875rem 1.25rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                  outline: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 4px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
                placeholder="contacto@tunegocio.com"
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                Teléfono
              </label>
              <input
                type="tel"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '0.875rem 1.25rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                  outline: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }}
                placeholder="+593 99 999 9999"
              />
            </div>
          </div>

          <div style={{ marginBottom: '1.5rem' }}>
            <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
              WhatsApp (opcional)
            </label>
            <input
              type="tel"
              name="whatsapp_number"
              value={formData.whatsapp_number}
              onChange={handleChange}
              style={{
                width: '100%',
                padding: '0.875rem 1.25rem',
                border: '2px solid #e5e7eb',
                borderRadius: '16px',
                fontSize: '0.875rem',
                transition: 'all 0.2s',
                outline: 'none',
                backgroundColor: 'rgba(255, 255, 255, 0.9)'
              }}
              placeholder="+593 99 999 9999"
            />
          </div>

          {/* Datos del Administrador */}
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '1rem',
            marginTop: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span>👤</span> Datos del Administrador
          </h3>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                Nombre *
              </label>
              <input
                type="text"
                name="nombre"
                value={formData.nombre}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '0.875rem 1.25rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                  outline: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }}
                placeholder="Tu nombre"
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                Apellido *
              </label>
              <input
                type="text"
                name="apellido"
                value={formData.apellido}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '0.875rem 1.25rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                  outline: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }}
                placeholder="Tu apellido"
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                Contraseña *
              </label>
              <PasswordInput
                name="password"
                value={formData.password}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '0.875rem 1.25rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                  outline: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
            
            <div>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151', fontSize: '0.875rem' }}>
                Confirmar Contraseña *
              </label>
              <PasswordInput
                name="confirmar_password"
                value={formData.confirmar_password}
                onChange={handleChange}
                required
                style={{
                  width: '100%',
                  padding: '0.875rem 1.25rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '16px',
                  fontSize: '0.875rem',
                  transition: 'all 0.2s',
                  outline: 'none',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }}
                placeholder="Repite la contraseña"
              />
            </div>
          </div>

          {/* Selección de Plan */}
          <h3 style={{
            fontSize: '1rem',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '1rem',
            marginTop: '1rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}>
            <span>📋</span> Selecciona tu Plan
          </h3>

          {/* Grid de planes — también son drop targets */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            {planes.map(plan => {
              const seleccionado = formData.plan_id === plan.id;
              const esPlanTrial = plan.nombre === 'Trial';
              const esDropTarget = dropTarget && seleccionado;
              return (
                <div
                  key={plan.id}
                  onClick={() => handlePlanSelect(plan.id)}
                  onDragOver={(e) => { e.preventDefault(); if (!esPlanTrial) setDropTarget(true); }}
                  onDragLeave={() => setDropTarget(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDropTarget(false);
                    if (draggedAddon && !esPlanTrial) {
                      handlePlanSelect(plan.id);
                      setAddons(prev => ({ ...prev, [draggedAddon]: true }));
                      setDraggedAddon(null);
                    }
                  }}
                  style={{
                    padding: '1.25rem',
                    borderRadius: '20px',
                    border: esDropTarget ? '2px dashed #3b82f6'
                          : seleccionado   ? '2px solid #3b82f6'
                          : '2px solid #e5e7eb',
                    backgroundColor: esDropTarget ? 'rgba(59,130,246,0.08)'
                                   : seleccionado  ? 'rgba(59,130,246,0.05)'
                                   : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative',
                  }}
                >
                  {seleccionado && (
                    <div style={{
                      position: 'absolute', top: '12px', right: '12px',
                      width: '20px', height: '20px', backgroundColor: '#3b82f6',
                      borderRadius: '50%', display: 'flex', alignItems: 'center',
                      justifyContent: 'center', color: 'white', fontSize: '12px'
                    }}>✓</div>
                  )}
                  <h4 style={{ fontSize: '1.1rem', fontWeight: '700', marginBottom: '0.5rem' }}>
                    {plan.nombre}
                  </h4>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <span style={{ fontSize: '1.5rem', fontWeight: '800', color: '#3b82f6' }}>
                      ${plan.precio_mensual}
                    </span>
                    <span style={{ fontSize: '0.875rem', color: '#6b7280' }}>/mes</span>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.75rem' }}>
                    {plan.descripcion}
                  </p>
                  <ul style={{ fontSize: '0.75rem', color: '#4b5563', listStyle: 'none', padding: 0, margin: 0 }}>
                    <li>✓ {plan.max_usuarios} usuario{plan.max_usuarios > 1 ? 's' : ''}</li>
                    <li>✓ {plan.max_clientes} clientes</li>
                    <li>✓ {plan.max_servicios} servicios</li>
                    {plan.tiene_sucursales   === true || plan.tiene_sucursales   === 1 ? <li>✓ Multi-sucursal</li>  : null}
                    {plan.tiene_profesionales === true || plan.tiene_profesionales === 1 ? <li>✓ Profesionales</li> : null}
                    {plan.tiene_reportes_avanzados === true || plan.tiene_reportes_avanzados === 1 ? <li>✓ Reportes avanzados</li> : null}
                  </ul>
                  {/* Addons agregados a este plan */}
                  {seleccionado && Object.values(addons).some(Boolean) && (
                    <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid #e5e7eb' }}>
                      <p style={{ fontSize: '0.7rem', color: '#6b7280', marginBottom: '0.4rem' }}>Funciones agregadas:</p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                        {ADDONS.filter(a => addons[a.key]).map(a => (
                          <span
                            key={a.key}
                            onClick={(e) => { e.stopPropagation(); toggleAddon(a.key); }}
                            title="Clic para quitar"
                            style={{
                              background: '#eff6ff', color: '#2563eb',
                              border: '1px solid #bfdbfe', borderRadius: '999px',
                              padding: '0.15rem 0.6rem', fontSize: '0.7rem',
                              fontWeight: '600', cursor: 'pointer', display: 'flex',
                              alignItems: 'center', gap: '0.25rem'
                            }}
                          >
                            {a.icon} {a.label} <span style={{ opacity: 0.6 }}>✕</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Zona drop visible cuando se arrastra */}
                  {esDropTarget && (
                    <div style={{
                      marginTop: '0.75rem', padding: '0.5rem', borderRadius: '10px',
                      background: 'rgba(59,130,246,0.08)', border: '1px dashed #93c5fd',
                      textAlign: 'center', fontSize: '0.75rem', color: '#3b82f6', fontWeight: '600'
                    }}>
                      ⬇ Suelta aquí para agregar
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* ── FUNCIONES ADICIONALES ── */}
          <div style={{
            borderTop: '2px dashed #e5e7eb',
            paddingTop: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{
              fontSize: '1rem', fontWeight: '600', color: '#374151',
              marginBottom: '0.4rem', display: 'flex', alignItems: 'center', gap: '0.5rem'
            }}>
              <span>🔧</span> Funciones Adicionales
            </h3>
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginBottom: '1rem' }}>
              {isTrial
                ? '⚠️ Los adicionales no están disponibles en el plan Trial.'
                : 'Arrastra al plan o haz clic para agregar. Puedes quitar haciendo clic en la etiqueta del plan.'}
            </p>
            <div style={{ display: 'flex', gap: '0.875rem', flexWrap: 'wrap' }}>
              {ADDONS.map(addon => {
                const activo = addons[addon.key];
                return (
                  <div
                    key={addon.key}
                    draggable={!isTrial}
                    onDragStart={() => { if (!isTrial) setDraggedAddon(addon.key); }}
                    onDragEnd={() => setDraggedAddon(null)}
                    onClick={() => toggleAddon(addon.key)}
                    style={{
                      padding: '0.875rem 1.1rem',
                      borderRadius: '16px',
                      border: activo ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                      background: activo ? 'rgba(59,130,246,0.06)' : 'white',
                      cursor: isTrial ? 'not-allowed' : 'grab',
                      opacity: isTrial ? 0.45 : 1,
                      transition: 'all 0.2s',
                      userSelect: 'none',
                      minWidth: '150px',
                      boxShadow: activo ? '0 4px 12px rgba(59,130,246,0.15)' : '0 1px 3px rgba(0,0,0,0.06)',
                    }}
                  >
                    <div style={{ fontSize: '1.4rem', marginBottom: '0.3rem' }}>{addon.icon}</div>
                    <div style={{ fontWeight: '700', fontSize: '0.875rem', color: activo ? '#2563eb' : '#374151', marginBottom: '0.2rem' }}>
                      {addon.label}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.4rem', lineHeight: 1.4 }}>
                      {addon.desc}
                    </div>
                    <div style={{ fontWeight: '800', fontSize: '1rem', color: activo ? '#2563eb' : '#3b82f6' }}>
                      +${addon.price}<span style={{ fontWeight: 400, fontSize: '0.75rem', color: '#6b7280' }}>/mes</span>
                    </div>
                    {activo && (
                      <div style={{ marginTop: '0.4rem', fontSize: '0.7rem', color: '#2563eb', fontWeight: '600' }}>
                        ✓ Agregado
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── RESUMEN DE PAGO ── */}
          <div style={{
            background: 'linear-gradient(135deg, #eff6ff, #f8fafc)',
            border: '1.5px solid #bfdbfe',
            borderRadius: '16px',
            padding: '1.25rem 1.5rem',
            marginBottom: '1.5rem'
          }}>
            <h3 style={{ fontSize: '0.9rem', fontWeight: '700', color: '#374151', marginBottom: '0.875rem' }}>
              📋 Resumen
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', fontSize: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', color: '#4b5563' }}>
                <span>Plan {planSeleccionado?.nombre || '—'}</span>
                <span>${precioBase.toFixed(2)}</span>
              </div>
              {ADDONS.filter(a => addons[a.key]).map(a => (
                <div key={a.key} style={{ display: 'flex', justifyContent: 'space-between', color: '#4b5563' }}>
                  <span>+ {a.label}</span>
                  <span>+${a.price.toFixed(2)}</span>
                </div>
              ))}
              <div style={{
                borderTop: '1.5px solid #bfdbfe', marginTop: '0.5rem', paddingTop: '0.5rem',
                display: 'flex', justifyContent: 'space-between',
                fontWeight: '800', fontSize: '1.05rem', color: '#1e40af'
              }}>
                <span>Total</span>
                <span>${totalFinal.toFixed(2)}<span style={{ fontSize: '0.8rem', fontWeight: 400, color: '#6b7280' }}>/mes</span></span>
              </div>
              {isTrial && (
                <div style={{ fontSize: '0.78rem', color: '#059669', fontWeight: '600', marginTop: '0.25rem' }}>
                  ✓ 30 días gratis — sin tarjeta de crédito
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '0.875rem',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '16px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              opacity: loading ? 0.7 : 1,
              boxShadow: '0 8px 14px -4px rgba(59, 130, 246, 0.4)',
              marginBottom: '1rem'
            }}
          >
            {loading ? 'Procesando...' : 'Crear Cuenta'}
          </button>
        </form>

        <div style={{
          textAlign: 'center',
          marginTop: '1.5rem',
          color: '#6b7280',
          fontSize: '0.875rem'
        }}>
          ¿Ya tienes cuenta?{' '}
          <Link
            to="/login"
            style={{
              color: '#3b82f6',
              textDecoration: 'none',
              fontWeight: '600'
            }}
          >
            Inicia sesión aquí
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Register;